import React, { useState, useMemo } from 'react';
import { useAdminUsers } from '../../hooks/useAdminUsers';
import { useEvents } from '../../hooks/useFirebase';
import { useAllStaffMembers } from '../../hooks/useStaffMembers';
import { getLimits, formatMax } from '../../utils/planLimits';
import { useConfirm } from '../../contexts/ConfirmContext';

const UsersPage = () => {
  const { users, loading, error, createUser, updateUser, deleteUser } = useAdminUsers();
  const confirm = useConfirm();
  const { events } = useEvents();
  const { staffByEvent } = useAllStaffMembers(events);

  // owner UID → { eventCount, staffCount } のマップ
  const ownerStats = useMemo(() => {
    const stats = {};
    Object.entries(events).forEach(([eventId, event]) => {
      const oid = event.owner_id;
      if (!oid) return;
      if (!stats[oid]) stats[oid] = { eventCount: 0, staffUids: new Set() };
      stats[oid].eventCount += 1;
      Object.keys(staffByEvent[eventId] || {}).forEach((uid) =>
        stats[oid].staffUids.add(uid)
      );
    });
    // Set → 数値に変換
    Object.values(stats).forEach((s) => {
      s.staffCount = s.staffUids.size;
      delete s.staffUids;
    });
    return stats;
  }, [events, staffByEvent]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showStaff, setShowStaff] = useState(false);
  const [detailUser, setDetailUser] = useState(null); // 使用状況詳細表示用

  // 新規ユーザー追加用ステート
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('owner');
  const [newPlan, setNewPlan] = useState('basic');
  const [newContact, setNewContact] = useState('');
  const [newMemo, setNewMemo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetCreateForm = () => {
    setNewEmail(''); setNewName(''); setNewRole('owner');
    setNewPlan('basic'); setNewContact(''); setNewMemo('');
  };

  // 編集用ステート（ライセンス＋メモ）
  const [editPlan, setEditPlan] = useState('basic');
  const [editStatus, setEditStatus] = useState('active');
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [editContact, setEditContact] = useState('');
  const [editMemo, setEditMemo] = useState('');

  const handleCreateUser = async () => {
    if (!newEmail || !newName || isSubmitting) return;
    setIsSubmitting(true);
    const res = await createUser({
      email: newEmail, displayName: newName, role: newRole,
      plan: newPlan, contact: newContact, memo: newMemo,
    });

    if (res.success) {
      setIsCreateModalOpen(false);
      resetCreateForm();
      alert('ユーザーを作成し、招待メールを送信しました。');
    } else if (res.authOnlyExists) {
      alert(`${newEmail} はすでに招待済みです。パスワード設定メールを再送しました。`);
      setIsCreateModalOpen(false);
      resetCreateForm();
    } else if (res.alreadyExists) {
      const existing = res.existingUser;
      const ok = await confirm({
        message: `${newEmail} はすでに「${existing.role}」として登録されています。\n「${newRole}」として追加登録しますか？\n\n※スタッフとして参加しているイベントはそのまま保持されます。`,
        type: 'info',
      });
      if (ok) {
        const updates = {
          role: newRole,
          display_name: newName || existing.display_name,
          ...(newContact.trim() && { contact: newContact.trim() }),
          ...(newMemo.trim() && { memo: newMemo.trim() }),
          ...(newRole === 'owner' && !existing.license ? {
            license: {
              plan: newPlan || 'basic',
              status: 'active',
              purchased_at: Date.now(),
              expires_at: Date.now() + 30 * 24 * 60 * 60 * 1000,
            },
          } : {}),
        };
        await updateUser(res.existingUid, updates);
        setIsCreateModalOpen(false);
        resetCreateForm();
      }
    } else {
      alert(`エラー: ${res.error}`);
    }
    setIsSubmitting(false);
  };

  const openEditModal = (uid, user) => {
    setSelectedUser({ uid, ...user });
    setEditPlan(user.license?.plan || 'basic');
    setEditStatus(user.license?.status || 'active');
    setEditContact(user.contact || '');
    setEditMemo(user.memo || '');
    if (user.license?.expires_at) {
      const date = new Date(user.license.expires_at);
      setEditExpiresAt(date.toISOString().split('T')[0]);
    } else {
      setEditExpiresAt('');
    }
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || isSubmitting) return;

    // プラン変更時: 新プランの上限を現在の使用数が超えていないか検証
    if (selectedUser.role === 'owner' && editPlan !== (selectedUser.license?.plan || 'basic')) {
      const newLimits = getLimits(editPlan);
      const usage = ownerStats[selectedUser.uid] || { eventCount: 0, staffCount: 0 };
      const overflow = [];
      if (usage.eventCount > newLimits.maxEvents) {
        overflow.push(`・イベント作成数：${usage.eventCount} → 上限 ${formatMax(newLimits.maxEvents)}`);
      }
      if (usage.staffCount > newLimits.maxStaff) {
        overflow.push(`・スタッフ招待数：${usage.staffCount} → 上限 ${formatMax(newLimits.maxStaff)}`);
      }
      if (overflow.length > 0) {
        await confirm({
          message:
            `「${editPlan.toUpperCase()}」プランの上限を超えるため変更できません。\n\n` +
            `${overflow.join('\n')}\n\n` +
            `先にイベント数・スタッフ数を上限以下に調整してから再度お試しください。`,
          type: 'warning',
        });
        return;
      }
    }

    setIsSubmitting(true);

    const updates = {
      contact: editContact,
      memo: editMemo,
      ...(selectedUser.role === 'owner' && {
        'license/plan': editPlan,
        'license/status': editStatus,
        'license/expires_at': editExpiresAt ? new Date(editExpiresAt).getTime() : null,
      }),
    };

    const res = await updateUser(selectedUser.uid, updates);
    if (res.success) {
      setIsEditModalOpen(false);
      setSelectedUser(null);
    } else {
      alert(`エラー: ${res.error}`);
    }
    setIsSubmitting(false);
  };

  const handleDelete = async (uid) => {
    const ok = await confirm({
      message: '本当にこのユーザーを削除しますか？\n※DBから削除されます。Firebase Authenticationのユーザーは手動で削除してください。',
      type: 'danger',
    });
    if (ok) await deleteUser(uid);
  };

  if (loading) return <div style={styles.loading}>読み込み中...</div>;
  if (error) return <div style={styles.error}>エラー: {error}</div>;

  const usersList = Object.entries(users)
    .map(([uid, user]) => ({ uid, ...user }))
    // 登録順（created_at 昇順）。未設定の古いユーザーは末尾にまとめる
    .sort((a, b) => (a.created_at ?? Infinity) - (b.created_at ?? Infinity));
  const ownerList = usersList.filter(u => u.role === 'owner' || u.role === 'superadmin');
  const staffList = usersList.filter(u => u.role === 'staff');
  const displayList = showStaff ? usersList : ownerList;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👥 ユーザー管理</h1>
          <p style={styles.subtitle}>システム全体のユーザーとオーナーライセンスを管理します</p>
        </div>
        <button onClick={() => setIsCreateModalOpen(true)} style={styles.primaryButton}>
          + 新規ユーザー追加
        </button>
      </div>

      {/* スタッフ表示トグル */}
      <div style={styles.filterRow}>
        <button
          onClick={() => setShowStaff(!showStaff)}
          style={{
            ...styles.toggleButton,
            background: showStaff ? '#6c63ff' : '#f1f5f9',
            color: showStaff ? '#fff' : '#475569',
          }}
        >
          {showStaff ? '👤 スタッフを非表示' : `👤 スタッフを表示 (${staffList.length})`}
        </button>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: '12%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '11%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={styles.th}>名前</th>
              <th style={styles.th}>メールアドレス</th>
              <th style={styles.th}>権限</th>
              <th style={styles.th}>ライセンス状況</th>
              <th style={styles.th}>使用状況</th>
              <th style={styles.th}>連絡先</th>
              <th style={styles.th}>メモ</th>
              <th style={styles.th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {displayList.map(user => (
              <tr key={user.uid} style={styles.tr}>
                <td style={styles.td}>
                  <div style={styles.userName}>{user.display_name || '未設定'}</div>
                </td>
                <td style={styles.td}>{user.email}</td>
                <td style={styles.td}>
                  <span style={getRoleBadgeStyle(user.role)}>{user.role}</span>
                </td>
                <td style={styles.td}>
                  {user.role === 'owner' ? (
                    <div>
                      <div style={styles.licensePlan}>{user.license?.plan?.toUpperCase() || 'NONE'}</div>
                      <div style={getLicenseStatusStyle(user.license?.status)}>
                        {user.license?.status === 'active' ? '● 有効' : '○ 無効'}
                      </div>
                    </div>
                  ) : (
                    <span style={styles.notApplicable}>-</span>
                  )}
                </td>
                <td style={styles.td}>
                  {user.role === 'owner' ? (() => {
                    const limits = getLimits(user.license?.plan);
                    const stats = ownerStats[user.uid] || { eventCount: 0, staffCount: 0 };
                    return (
                      <div
                        style={{ ...styles.statsCell, cursor: 'pointer' }}
                        onClick={() => setDetailUser({ uid: user.uid, ...user })}
                        title="クリックで詳細を表示"
                      >
                        <span style={stats.eventCount >= limits.maxEvents ? styles.statOver : styles.stat}>
                          📋 {stats.eventCount} / {formatMax(limits.maxEvents)}
                        </span>
                        <span style={stats.staffCount >= limits.maxStaff ? styles.statOver : styles.stat}>
                          👤 {stats.staffCount} / {formatMax(limits.maxStaff)}
                        </span>
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>詳細 ↗</span>
                      </div>
                    );
                  })() : (
                    <span style={styles.notApplicable}>-</span>
                  )}
                </td>
                <td style={styles.td}>
                  {renderContact(user.contact)}
                </td>
                <td style={styles.td}>
                  {user.memo
                    ? <span style={styles.memoCell} title={user.memo}>{user.memo}</span>
                    : <span style={{ color: '#94a3b8' }}>-</span>}
                </td>
                <td style={styles.td}>
                  <div style={styles.actions}>
                    <button
                      onClick={() => openEditModal(user.uid, user)}
                      style={styles.actionButton}
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDelete(user.uid)}
                      style={{ ...styles.actionButton, color: '#ef4444', borderColor: '#fee2e2' }}
                    >
                      削除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {displayList.length === 0 && (
              <tr>
                <td colSpan="8" style={styles.emptyState}>ユーザーが見つかりません</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 新規ユーザー追加モーダル */}
      {isCreateModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>新規ユーザー追加</h2>
            <p style={styles.modalSubText}>
              招待メールを送信し、本人がパスワードを設定できるようにします。
            </p>

            <label style={styles.label}>名前 <span style={styles.required}>*必須</span></label>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              style={styles.input}
              placeholder="例: 山田 太郎"
            />

            <label style={styles.label}>メールアドレス <span style={styles.required}>*必須</span></label>
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              style={styles.input}
              placeholder="例: user@example.com"
            />

            <label style={styles.label}>権限</label>
            <select
              value={newRole}
              onChange={e => setNewRole(e.target.value)}
              style={styles.select}
            >
              <option value="owner">Owner (テナント管理者)</option>
              <option value="superadmin">Superadmin (システム管理者)</option>
            </select>

            {newRole === 'owner' && (
              <>
                <label style={styles.label}>ライセンス（プラン）</label>
                <select
                  value={newPlan}
                  onChange={e => setNewPlan(e.target.value)}
                  style={styles.select}
                >
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="trial">Trial</option>
                </select>
              </>
            )}

            <label style={styles.label}>連絡先</label>
            <input
              type="text"
              value={newContact}
              onChange={e => setNewContact(e.target.value)}
              style={styles.input}
              placeholder="X URL（https://x.com/...）や連絡先など"
            />

            <label style={styles.label}>メモ</label>
            <textarea
              value={newMemo}
              onChange={e => setNewMemo(e.target.value)}
              style={styles.textarea}
              placeholder="支払い方法・備考など"
              rows={3}
            />

            <div style={styles.modalActions}>
              <button onClick={() => { setIsCreateModalOpen(false); resetCreateForm(); }} style={styles.secondaryButton}>キャンセル</button>
              <button
                onClick={handleCreateUser}
                style={{ ...styles.primaryButton, opacity: (!newEmail || !newName || isSubmitting) ? 0.5 : 1 }}
                disabled={!newEmail || !newName || isSubmitting}
              >
                {isSubmitting ? '処理中...' : 'ユーザー作成＆招待'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 編集モーダル（ライセンス＋メモ） */}
      {isEditModalOpen && selectedUser && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>ユーザー編集</h2>
            <p style={styles.modalSubText}>{selectedUser.display_name} ({selectedUser.email})</p>

            {selectedUser.role === 'owner' && (
              <>
                <label style={styles.label}>プラン</label>
                <select
                  value={editPlan}
                  onChange={e => setEditPlan(e.target.value)}
                  style={styles.select}
                >
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="trial">Trial</option>
                </select>

                <label style={styles.label}>ステータス</label>
                <select
                  value={editStatus}
                  onChange={e => setEditStatus(e.target.value)}
                  style={styles.select}
                >
                  <option value="active">有効 (Active)</option>
                  <option value="inactive">無効 (Inactive)</option>
                </select>

                <label style={styles.label}>有効期限</label>
                <input
                  type="date"
                  value={editExpiresAt}
                  onChange={e => setEditExpiresAt(e.target.value)}
                  style={styles.input}
                />
              </>
            )}

            <label style={styles.label}>連絡先</label>
            <input
              type="text"
              value={editContact}
              onChange={e => setEditContact(e.target.value)}
              style={styles.input}
              placeholder="X URL（https://x.com/...）や連絡先など"
            />
            <p style={{ fontSize: '12px', color: '#94a3b8', margin: '-12px 0 20px' }}>
              ※ URLを入力すると一覧でクリック可能なリンクになります
            </p>

            <label style={styles.label}>メモ</label>
            <textarea
              value={editMemo}
              onChange={e => setEditMemo(e.target.value)}
              style={styles.textarea}
              placeholder="支払い方法・備考など"
              rows={3}
            />

            <div style={styles.modalActions}>
              <button onClick={() => setIsEditModalOpen(false)} style={styles.secondaryButton}>キャンセル</button>
              <button
                onClick={handleUpdateUser}
                style={styles.primaryButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 使用状況詳細モーダル */}
      {detailUser && (() => {
        const ownerEvents = Object.entries(events)
          .filter(([, e]) => e.owner_id === detailUser.uid)
          .map(([id, e]) => ({ id, ...e }));

        return (
          <div style={styles.modalOverlay} onClick={() => setDetailUser(null)}>
            <div style={{ ...styles.modalContent, maxWidth: '560px', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <h2 style={styles.modalTitle}>管理状況の詳細</h2>
                  <p style={styles.modalSubText}>{detailUser.display_name}（{detailUser.email}）</p>
                </div>
                <button onClick={() => setDetailUser(null)} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#94a3b8', cursor: 'pointer', padding: '0 4px' }}>×</button>
              </div>

              {ownerEvents.length === 0 ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: '24px 0' }}>イベントがありません</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {ownerEvents.map(event => {
                    const members = staffByEvent[event.id] || {};
                    const memberList = Object.entries(members);
                    return (
                      <div key={event.id} style={{ border: '1px solid #e8edf5', borderRadius: '10px', overflow: 'hidden' }}>
                        {/* イベントヘッダー */}
                        <div style={{ background: '#f8fafc', padding: '10px 16px', borderBottom: memberList.length > 0 ? '1px solid #e8edf5' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '800', fontSize: '14px', color: '#0f172a' }}>{event.event_name}</span>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>スタッフ {memberList.length}人</span>
                            <span style={{
                              fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px',
                              background: event.status === 'matched' ? '#dcfce7' : event.status === 'matching' ? '#ede9fe' : '#fef3c7',
                              color: event.status === 'matched' ? '#166534' : event.status === 'matching' ? '#5b21b6' : '#92400e',
                            }}>
                              {event.status === 'matched' ? 'マッチ済' : event.status === 'matching' ? '受付中' : '準備中'}
                            </span>
                          </div>
                        </div>
                        {/* スタッフ一覧 */}
                        {memberList.length > 0 ? (
                          <div style={{ padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {memberList.map(([uid, member]) => (
                              <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                                <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: '#ede9fe', color: '#6c63ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '11px', flexShrink: 0 }}>
                                  {(member.display_name || '?').charAt(0).toUpperCase()}
                                </div>
                                <span style={{ color: '#1e293b', fontWeight: '600' }}>{member.display_name || uid}</span>
                                {member.memo && <span style={{ color: '#94a3b8', fontSize: '11px' }}>— {member.memo}</span>}
                                <span style={{ color: '#cbd5e1', fontSize: '11px', marginLeft: 'auto' }}>
                                  {member.joined_at ? new Date(member.joined_at).toLocaleDateString('ja-JP') : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ padding: '10px 16px', fontSize: '12px', color: '#94a3b8' }}>スタッフなし</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// 連絡先を表示。URLならクリック可能なリンクにする
const renderContact = (contact) => {
  if (!contact) return <span style={{ color: '#94a3b8' }}>-</span>;

  const trimmed = contact.trim();
  const isUrl = /^https?:\/\/\S+$/i.test(trimmed);

  if (isUrl) {
    return (
      <a
        href={trimmed}
        target="_blank"
        rel="noreferrer"
        style={{
          fontSize: '13px', color: '#6c63ff', fontWeight: '600',
          textDecoration: 'none',
          display: 'block', maxWidth: '220px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        onClick={(e) => e.stopPropagation()}
        title={trimmed}
      >
        🔗 {trimmed.replace(/^https?:\/\//, '')}
      </a>
    );
  }

  return (
    <span
      style={{
        fontSize: '13px', color: '#475569',
        display: 'block', maxWidth: '220px',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}
      title={trimmed}
    >
      {trimmed}
    </span>
  );
};

const getRoleBadgeStyle = (role) => {
  const base = { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', display: 'inline-block' };
  switch (role?.trim()?.toLowerCase()) {
    case 'superadmin': return { ...base, background: '#fef08a', color: '#854d0e' };
    case 'owner': return { ...base, background: '#dbeafe', color: '#1e40af' };
    case 'staff': return { ...base, background: '#f3f4f6', color: '#374151' };
    default: return { ...base, background: '#fee2e2', color: '#991b1b' };
  }
};

const getLicenseStatusStyle = (status) => ({
  fontSize: '12px',
  fontWeight: '600',
  color: status === 'active' ? '#10b981' : '#ef4444',
  marginTop: '4px',
});

const styles = {
  container: { padding: '40px', maxWidth: '1400px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  title: { fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 8px 0' },
  subtitle: { fontSize: '15px', color: '#64748b', margin: 0 },
  filterRow: { marginBottom: '20px' },
  toggleButton: {
    border: 'none',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  primaryButton: {
    background: '#6c63ff', color: '#fff', border: 'none', padding: '12px 20px',
    borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
  },
  secondaryButton: {
    background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px 20px',
    borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
  },
  tableContainer: {
    background: '#fff', borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)', overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  th: {
    padding: '14px 16px', background: '#f8fafc', fontSize: '12px', fontWeight: '700',
    color: '#475569', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '14px 16px', verticalAlign: 'middle' },
  userName: { fontWeight: '600', color: '#1e293b', fontSize: '15px' },
  licensePlan: { fontSize: '13px', fontWeight: '700', color: '#475569' },
  notApplicable: { color: '#94a3b8', fontSize: '14px' },
  memoCell: { fontSize: '13px', color: '#475569', display: 'block', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  statsCell: { display: 'flex', flexDirection: 'column', gap: '3px', whiteSpace: 'nowrap' },
  stat: { fontSize: '12px', color: '#475569', fontWeight: '600', whiteSpace: 'nowrap' },
  statOver: { fontSize: '12px', color: '#ef4444', fontWeight: '700', whiteSpace: 'nowrap' },
  actions: { display: 'flex', gap: '6px', flexWrap: 'nowrap' },
  actionButton: {
    background: 'transparent', border: '1px solid #e2e8f0', color: '#475569',
    padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  emptyState: { textAlign: 'center', padding: '40px', color: '#64748b' },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)',
  },
  modalContent: {
    background: '#fff', padding: '32px', borderRadius: '16px', width: '100%', maxWidth: '420px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', margin: '0 20px', maxHeight: '90vh', overflowY: 'auto',
  },
  modalTitle: { fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px 0' },
  modalSubText: { fontSize: '14px', color: '#64748b', marginBottom: '24px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' },
  required: { color: '#ef4444', fontSize: '11px', fontWeight: '700', marginLeft: '4px' },
  input: {
    width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
    fontSize: '15px', marginBottom: '20px', outline: 'none', boxSizing: 'border-box',
  },
  select: {
    width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
    fontSize: '15px', marginBottom: '20px', outline: 'none', boxSizing: 'border-box',
    background: '#fff', cursor: 'pointer',
  },
  textarea: {
    width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
    fontSize: '14px', marginBottom: '20px', outline: 'none', boxSizing: 'border-box',
    resize: 'vertical', fontFamily: 'inherit',
  },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' },
  loading: { padding: '40px', textAlign: 'center', color: '#64748b' },
  error: { padding: '40px', textAlign: 'center', color: '#ef4444' },
};

export default UsersPage;
